import React from 'react';
import { motion } from 'framer-motion';
import { User } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import Button from '../ui/Button';
import NotificationBell from '../common/NotificationBell';

interface HeaderProps {
  pageTitle?: string;
  onMenuClick?: () => void;
}

/**
 * Header de l'application avec titre dynamique
 * Affiche le titre de la page courante et les actions utilisateur
 */
const Header = ({ pageTitle = 'Dashboard', onMenuClick }: HeaderProps) => {
  const { user, userProfile, logout } = useAuth();

  // Fonction pour obtenir le nom d'affichage
  const getDisplayName = () => {
    if (userProfile?.displayName) {
      return userProfile.displayName;
    }
    if (user?.email) {
      // Extraire la partie avant @ de l'email et capitaliser
      const emailName = user.email.split('@')[0];
      return emailName.charAt(0).toUpperCase() + emailName.slice(1);
    }
    return 'Utilisateur';
  };

  return (
    <motion.header
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white shadow-sm border-b border-gray-200 px-6 py-4"
    >
      <div className="flex items-center justify-between">
        <div>
          {/* Logo remplace le titre */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.1 }}
            transition={{ duration: 0.3 }}
            className="w-56 h-16 flex items-center justify-center overflow-hidden cursor-pointer"
            onClick={() => window.location.href = '/dashboard'}
          >
            <img 
              src="/logo.png" 
              alt="EspaceResto Logo" 
              className="w-full h-full object-contain"
              style={{ backgroundColor: 'transparent' }}
            />
          </motion.div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <NotificationBell />

          {/* User Menu */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm font-medium text-gray-700">
                {getDisplayName()}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
            >
              Déconnexion
            </Button>
          </div>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;